// frontend/src/AccessibilityAudit/IssueRow.jsx — one failing axe rule or interaction check, expanding (Enter / Space,
// aria-expanded) to its elements. Elements are grouped across pages and devices by the API (report.group).
import React, { useEffect, useState } from 'react';
import { a11yApi } from './apiClient';

export const SEV = ['critical', 'serious', 'moderate', 'minor'];
export const SEV_LABEL = { critical: 'Critical', serious: 'Serious', moderate: 'Moderate', minor: 'Minor' };
const SHOW_FIRST = 6;
const devLabel = (d) => (d === 'phone' ? 'Phone' : 'Desktop');

function Screenshot({ findingId, alt }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url = null; let live = true;
    setSrc(null); setFailed(false);
    a11yApi.screenshot(findingId).then((u) => { url = u; if (live) setSrc(u); else URL.revokeObjectURL(u); }, () => live && setFailed(true));
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [findingId]);
  if (failed) return <span className="uh-faint">Screenshot not available.</span>;
  if (!src) return <span className="uh-faint">Loading screenshot…</span>;
  return <img src={src} alt={alt} />;
}

function Copy({ text }) {
  const [done, setDone] = useState(false);
  const copy = () => {
    const ok = () => { setDone(true); setTimeout(() => setDone(false), 1200); };
    try { navigator.clipboard.writeText(text).then(ok, () => {}); } catch { /* old browser: the text is selectable */ }
  };
  return <button type="button" className="uh-ax-copy" onClick={copy}>{done ? 'Copied' : 'Copy'}</button>;
}

function ElementCard({ el, device, base }) {
  const shotDevs = Object.keys(el.shots).filter((d) => device === 'both' || d === device);
  const [pick, setPick] = useState(null);
  const cur = shotDevs.includes(pick) ? pick : shotDevs[0];
  return (
    <div className={`uh-ax-el${cur ? '' : ' noshot'}`}>
      <div className="uh-ax-el-i">
        <div className="uh-ax-row">
          {el.devices.map((d) => <span key={d} className="uh-ax-chip dev">{devLabel(d)}</span>)}
          <span className={`uh-ax-chip${el.source === 'Theme' ? '' : ' app'}`}>{el.source === 'Theme' ? 'Theme' : `App: ${el.source}`}</span>
          {el.pages.length > 1 && <span className="uh-ax-chip">on {el.pages.length} pages</span>}
          {el.new && <span className="uh-ax-chip new">New</span>}
        </div>
        <dl className="uh-ax-kv">
          <dt>Selector</dt><dd className="uh-ax-sel"><code>{el.selector}</code><Copy text={el.selector} /></dd>
          {el.html && <><dt>HTML</dt><dd><pre className="html" tabIndex={0} aria-label="HTML of the element">{el.html}</pre></dd></>}
          <dt>{el.summary ? 'What we saw' : "What's wrong"}</dt>
          <dd className="uh-ax-fail">{el.failure}{el.summary && el.summary !== el.failure && <><br /><small>{el.summary}</small></>}</dd>
          <dt>Pages</dt>
          <dd className="uh-ax-pages">{el.pages.map((p) => <a key={p} href={base + p} target="_blank" rel="noopener noreferrer">{p} ↗</a>)}</dd>
        </dl>
      </div>
      {cur && (
        <div className="uh-ax-shot">
          {shotDevs.length > 1
            ? <div className="uh-ax-seg" role="group" aria-label="Screenshot device">
                {shotDevs.map((d) => <button type="button" key={d} aria-pressed={d === cur} onClick={() => setPick(d)}>{devLabel(d)}</button>)}
              </div>
            : <div />}
          <figure>
            <Screenshot findingId={el.shots[cur]} alt={`Screenshot of the failing element on ${el.pages[0]}, ${devLabel(cur).toLowerCase()}, outlined in red`} />
            <figcaption>{devLabel(cur)} · outlined in red</figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}

export default function IssueRow({ rule, device, base, review }) {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(false);
  const pages = new Set(); const devs = new Set(); const apps = new Set();
  rule.els.forEach((e) => { e.pages.forEach((p) => pages.add(p)); e.devices.forEach((d) => devs.add(d)); if (e.source !== 'Theme') apps.add(e.source); });
  const sev = rule.impact || 'none';
  const shown = all ? rule.els : rule.els.slice(0, SHOW_FIRST);
  return (
    <div className={`uh-ax-rule sev-${sev}`}>
      <button type="button" className="uh-ax-rule-h" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="uh-ax-stripe" /><span className="uh-ax-chev" aria-hidden="true">›</span>
        <span>
          <span className="uh-ax-rule-t">{rule.help}</span>
          <span className="uh-ax-rule-s">
            <span className="uh-ax-pill">{rule.impact ? SEV_LABEL[rule.impact] : 'Needs review'}</span>
            {rule.kind === 'interaction' ? <span className="uh-ax-chip ix">Interaction check</span> : <code>{rule.id}</code>}
            {rule.wcag.map((w) => <span key={w.id} className="uh-ax-chip">{w.id} {w.name}</span>)}
            {devs.size === 1 && <span className="uh-ax-chip dev">{devs.has('phone') ? 'Phone only' : 'Desktop only'}</span>}
            {[...apps].map((a) => <span key={a} className="uh-ax-chip app">{a}</span>)}
            {!review && rule.new && <span className="uh-ax-chip new">New</span>}
          </span>
        </span>
        <span className="uh-ax-rule-n"><b>{rule.els.length}</b> element{rule.els.length === 1 ? '' : 's'}<br />{pages.size} page{pages.size === 1 ? '' : 's'}</span>
      </button>
      {open && (
        <div className="uh-ax-rule-b">
          <div className="uh-ax-desc"><span>{rule.description}</span>{rule.helpUrl && <a href={rule.helpUrl} target="_blank" rel="noopener noreferrer">How to fix ↗</a>}</div>
          {shown.map((e) => <ElementCard key={e.key} el={e} device={device} base={base} />)}
          {rule.els.length > shown.length && <button type="button" className="uh-btn slim" style={{ justifySelf: 'start' }} onClick={() => setAll(true)}>Show {rule.els.length - shown.length} more</button>}
        </div>
      )}
    </div>
  );
}
